import { useMemo, useState, type ReactElement } from 'react';
import { Plus, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useAdminIdentifierTypeMutations,
  useAdminIdentifierTypes,
  type AdminIdentifierTypeInput,
  type AdminIdentifierTypeViewModel,
  type UpdateAdminIdentifierTypeInput,
} from '@/features/admin-identifier-types/api';
import { AdminIdentifierTypesTable } from '@/features/admin-identifier-types/components/AdminIdentifierTypesTable';
import { CreateIdentifierTypeDialog } from '@/features/admin-identifier-types/components/CreateIdentifierTypeDialog';
import { DeleteIdentifierTypeConfirmDialog } from '@/features/admin-identifier-types/components/DeleteIdentifierTypeConfirmDialog';
import { EditIdentifierTypeDialog } from '@/features/admin-identifier-types/components/EditIdentifierTypeDialog';

export function AdminGeneralPage(): ReactElement {
  const { t } = useTranslation();
  const query = useAdminIdentifierTypes();
  const { create, update, remove } = useAdminIdentifierTypeMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<AdminIdentifierTypeViewModel | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminIdentifierTypeViewModel | null>(null);

  const rows = useMemo(
    () =>
      query.isSuccess
        ? [...query.data].sort((a, b) => a.label.localeCompare(b.label))
        : [],
    [query.isSuccess, query.data],
  );

  function handleCreate(input: AdminIdentifierTypeInput): void {
    create.mutate(input, {
      onSuccess: () => {
        setCreateOpen(false);
        create.reset();
      },
    });
  }

  function handleEditSubmit(input: UpdateAdminIdentifierTypeInput): void {
    update.mutate(input, {
      onSuccess: () => {
        setPendingEdit(null);
        update.reset();
      },
    });
  }

  function handleDeleteConfirm(row: AdminIdentifierTypeViewModel): void {
    remove.mutate(row.id, {
      onSuccess: () => {
        setPendingDelete(null);
        remove.reset();
      },
    });
  }

  function handleDeleteCancel(): void {
    if (remove.isPending) return;
    setPendingDelete(null);
    remove.reset();
  }

  function handleEditCancel(): void {
    if (update.isPending) return;
    setPendingEdit(null);
    update.reset();
  }

  function handleTogglePrimary(row: AdminIdentifierTypeViewModel): void {
    if (row.isPrimary) return;
    update.mutate({ ...row, isPrimary: true });
  }

  function handleToggleEnforceFormat(row: AdminIdentifierTypeViewModel): void {
    update.mutate({ ...row, enforceFormat: !row.enforceFormat });
  }

  function handleToggleAllowDuplicates(row: AdminIdentifierTypeViewModel): void {
    update.mutate({ ...row, allowDuplicates: !row.allowDuplicates });
  }

  function handleToggleActive(row: AdminIdentifierTypeViewModel): void {
    update.mutate({ ...row, active: !row.active });
  }

  return (
    <>
      <PageHeader
        icon={<Settings className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.general.heading')}
        subtitle={t('routes.admin.general.subheading')}
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        <section aria-labelledby="admin-general-identifiers-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="admin-general-identifiers-heading"
                className="text-base font-semibold text-foreground"
              >
                {t('routes.admin.general.identifiers.heading')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('routes.admin.general.identifiers.subheading')}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                create.reset();
                setCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('routes.admin.general.identifiers.addAction')}
            </Button>
          </header>

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t('routes.admin.general.identifiers.error.heading')}
                message={t('routes.admin.general.identifiers.error.message')}
                codeLabel={t('routes.admin.general.identifiers.error.codeLabel')}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t('routes.admin.general.identifiers.error.retry')}
              />
            </div>
          )}

          {query.isSuccess && rows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t('routes.admin.general.identifiers.empty')}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t('routes.admin.general.identifiers.heading')}>
              <AdminIdentifierTypesTable
                rows={rows}
                onEdit={(row) => {
                  update.reset();
                  setPendingEdit(row);
                }}
                onDelete={(row) => {
                  remove.reset();
                  setPendingDelete(row);
                }}
                onTogglePrimary={handleTogglePrimary}
                onToggleEnforceFormat={handleToggleEnforceFormat}
                onToggleAllowDuplicates={handleToggleAllowDuplicates}
                onToggleActive={handleToggleActive}
                pendingDeleteId={
                  remove.isPending && pendingDelete !== null ? pendingDelete.id : null
                }
                pendingUpdateId={update.isPending ? update.variables.id : null}
              />
            </DataTable>
          )}
        </section>
      </div>

      <CreateIdentifierTypeDialog
        open={createOpen}
        onOpenChange={(next) => {
          if (!next && create.isPending) return;
          if (!next) create.reset();
          setCreateOpen(next);
        }}
        onSubmit={handleCreate}
        pending={create.isPending}
        submitError={create.error}
      />

      <EditIdentifierTypeDialog
        row={pendingEdit}
        onCancel={handleEditCancel}
        onSubmit={handleEditSubmit}
        pending={update.isPending}
        submitError={update.error}
      />

      <DeleteIdentifierTypeConfirmDialog
        row={pendingDelete}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        pending={remove.isPending}
        submitError={remove.error}
      />
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
